import { Component, HostListener, OnInit, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { ApiSurveyDto, ApiSurveyModelService, BusinessRulesModel } from '../../../../shared/services/api-surveymodel.service';
import { ApiSurveyBusinessRulesService, ApiPostSurveyBusinessRulesResponse } from '../../../../shared/services/api-survey-businessrules.service';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { CanDeactivateComponent } from '../survey-builder/survey-builder.guard';
import { CmdModel } from '../../models/cmd.model';
import { InfoTooltipComponent } from '../../../../shared/components/ui/info-tooltip/info-tooltip.component';

interface ParamEntry {
  key: string;
  value: string;
}

type SegmentMode = 'accept' | 'reject';

type EditableBusinessRule = BusinessRulesModel & {
  paramEntries: ParamEntry[];
  editingValueIndex: number | null;
  segmentMode: SegmentMode | null;
};

@Component({
  selector: 'app-survey-business-rules',
  templateUrl: './survey-business-rules.component.html',
  styleUrls: ['./survey-business-rules.component.css'],
  imports: [FormsModule, BreadcrumbComponent, PageLoaderComponent, SurveyHeaderComponent, InfoTooltipComponent]
})
export class SurveyBusinessRulesComponent implements OnInit, CanDeactivateComponent {
  survey: ApiSurveyDto | null = null;
  rules: EditableBusinessRule[] = [];
  loading = signal(false);
  error = signal<string | null>(null);

  private organizationId = '';
  private surveyId = '';

  readonly canEdit = computed(() => this.userStore.isAdmin());

  // change tracking
  private originalRules = new Map<string, string>();

  // update
  showUpdateConfirm = signal(false);
  updating = signal(false);
  updateResult = signal<ApiPostSurveyBusinessRulesResponse | null>(null);
  updateError = signal<string | null>(null);

  // segment scope picker
  openSegmentPickerRuleName: string | null = null;

  // discard
  showDiscardConfirm = signal(false);

  // unsaved-changes leave guard
  showLeaveConfirm = signal(false);
  private leaveResolve: ((allow: boolean) => void) | null = null;

  constructor(
    private route: ActivatedRoute,
    private organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private surveyModelService: ApiSurveyModelService,
    private businessRulesService: ApiSurveyBusinessRulesService) {}

  ngOnInit() {
    // surveySelectedGuard on the route guarantees these are present and valid.
    this.organizationId = this.organizationStore.selectedOrganizationId()!;
    this.surveyId = this.route.snapshot.paramMap.get('id')!;
    this.loadSurveyModel();
  }

  private loadSurveyModel(): void {
    this.loading.set(true);
    this.error.set(null);

    this.surveyModelService.getSurveyModel(this.organizationId, this.surveyId).subscribe({
      next: (survey) => {
        this.survey = survey;
        this.rules = (survey.businessRules ?? []).map(rule => this.toEditableRule(rule));
        this.takeSnapshot();
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load business rules. | ' + err);
        this.loading.set(false);
      }
    });
  }

  private toEditableRule(rule: BusinessRulesModel): EditableBusinessRule {
    return {
      ...rule,
      paramEntries: Object.entries(rule.parameters ?? {}).map(([key, value]) => ({ key, value })),
      editingValueIndex: null,
      segmentMode: this.computeSegmentMode(rule.segmentScope)
    };
  }

  private computeSegmentMode(segmentScope: BusinessRulesModel['segmentScope']): SegmentMode | null {
    if (segmentScope?.accept?.length) return 'accept';
    if (segmentScope?.reject?.length) return 'reject';
    return null;
  }

  // ── Change tracking ─────────────────────────────────────────────────────────

  private snapshotOf(rule: EditableBusinessRule): string {
    return JSON.stringify({
      enabled: rule.enabled,
      parameters: rule.parameters,
      values: rule.values,
      field: rule.field,
      priority: rule.priority,
      segmentScope: this.normalizeSegmentScope(rule.segmentScope)
    });
  }

  // Picking accept/reject without adding any segments isn't a real change, and should
  // never be sent to the API as an empty {accept:[], reject:[]} shell — collapse to null.
  private normalizeSegmentScope(segmentScope: BusinessRulesModel['segmentScope']): BusinessRulesModel['segmentScope'] {
    const accept = segmentScope?.accept ?? [];
    const reject = segmentScope?.reject ?? [];
    return accept.length === 0 && reject.length === 0 ? null : { accept, reject };
  }

  private takeSnapshot(): void {
    this.originalRules.clear();
    for (const rule of this.rules) {
      this.originalRules.set(rule.name, this.snapshotOf(rule));
    }
  }

  getRuleChangeStatus(rule: EditableBusinessRule): 'modified' | 'clean' {
    const original = this.originalRules.get(rule.name);
    return original === this.snapshotOf(rule) ? 'clean' : 'modified';
  }

  get pendingChangesCount(): number {
    return this.rules.filter(r => this.getRuleChangeStatus(r) !== 'clean').length;
  }

  // ── Editing (Admin only) ─────────────────────────────────────────────────────

  private syncParameters(rule: EditableBusinessRule): void {
    const record: Record<string, string> = {};
    for (const entry of rule.paramEntries) {
      if (entry.key.trim()) record[entry.key] = entry.value;
    }
    rule.parameters = record;
  }

  setEnabled(rule: EditableBusinessRule, enabled: boolean): void {
    if (!this.canEdit()) return;
    rule.enabled = enabled;
  }

  get cmdFieldOptions(): CmdModel[] {
    return this.survey?.cmd ?? [];
  }

  // A field stored on the rule but no longer present in the survey's CMD list is a
  // backend misconfiguration; keep it in the model but flag it in the UI.
  isUnknownField(rule: EditableBusinessRule): boolean {
    return !!rule.field && !this.cmdFieldOptions.some(cmd => cmd.dbColumn === rule.field);
  }

  setField(rule: EditableBusinessRule, field: string | null): void {
    if (!this.canEdit()) return;
    rule.field = field?.trim() ? field : null;
  }

  setPriority(rule: EditableBusinessRule, isOn: boolean): void {
    if (!this.canEdit()) return;
    rule.priority = isOn ? 1 : 0;
  }

  onParameterChange(rule: EditableBusinessRule): void {
    if (!this.canEdit()) return;
    this.syncParameters(rule);
  }

  addParameter(rule: EditableBusinessRule): void {
    if (!this.canEdit()) return;
    rule.paramEntries.push({ key: '', value: '' });
  }

  removeParameter(rule: EditableBusinessRule, entry: ParamEntry): void {
    if (!this.canEdit()) return;
    rule.paramEntries = rule.paramEntries.filter(e => e !== entry);
    this.syncParameters(rule);
  }

  addValue(rule: EditableBusinessRule): void {
    if (!this.canEdit()) return;
    rule.values = [...rule.values, ''];
    this.startEditingValue(rule, rule.values.length - 1);
  }

  removeValue(rule: EditableBusinessRule, index: number): void {
    if (!this.canEdit()) return;
    rule.values = rule.values.filter((_, i) => i !== index);
    rule.editingValueIndex = null;
  }

  startEditingValue(rule: EditableBusinessRule, index: number): void {
    if (!this.canEdit()) return;
    rule.editingValueIndex = index;
    setTimeout(() => {
      const el = document.getElementById(this.valueInputId(rule, index));
      (el as HTMLInputElement | null)?.focus();
    });
  }

  stopEditingValue(rule: EditableBusinessRule): void {
    const index = rule.editingValueIndex;
    rule.editingValueIndex = null;
    if (index === null) return;

    // Drop the tag if the user left it empty (e.g. hit "+ Add" then focused away without typing).
    if (!rule.values[index]?.trim()) {
      rule.values = rule.values.filter((_, i) => i !== index);
    }
  }

  valueInputId(rule: EditableBusinessRule, index: number): string {
    return `value-${rule.name}-${index}`;
  }

  // ── Segment scope (Admin only) ────────────────────────────────────────────

  get availableSegments(): string[] {
    return this.organizationStore.selectedOrganization()?.segments ?? [];
  }

  segmentValues(rule: EditableBusinessRule): string[] {
    return rule.segmentMode ? (rule.segmentScope?.[rule.segmentMode] ?? []) : [];
  }

  isSegmentSelected(rule: EditableBusinessRule, segment: string): boolean {
    return this.segmentValues(rule).includes(segment);
  }

  // A segment stored on the rule but no longer present in the organization's segment list
  // is a backend misconfiguration; keep it in the model but flag it in the UI.
  isUnknownSegment(segment: string): boolean {
    return !this.availableSegments.includes(segment);
  }

  setSegmentMode(rule: EditableBusinessRule, mode: SegmentMode): void {
    if (!this.canEdit() || rule.segmentMode === mode) return;
    const previousMode = rule.segmentMode;
    rule.segmentScope = rule.segmentScope ?? {};

    // Only one of accept/reject can hold values, so move the existing tags over to the newly selected one.
    const previousValues = previousMode ? (rule.segmentScope[previousMode] ?? []) : [];
    rule.segmentScope[mode] = previousValues;
    if (previousMode) rule.segmentScope[previousMode] = [];

    rule.segmentMode = mode;
  }

  toggleSegmentValue(rule: EditableBusinessRule, segment: string): void {
    if (!this.canEdit() || !rule.segmentMode) return;
    const mode = rule.segmentMode;
    rule.segmentScope = rule.segmentScope ?? {};
    const current = rule.segmentScope[mode] ?? [];
    rule.segmentScope[mode] = current.includes(segment)
      ? current.filter(s => s !== segment)
      : [...current, segment];
  }

  removeSegmentTag(rule: EditableBusinessRule, segment: string): void {
    if (!this.canEdit() || !rule.segmentMode) return;
    const mode = rule.segmentMode;
    rule.segmentScope![mode] = (rule.segmentScope?.[mode] ?? []).filter(s => s !== segment);
  }

  get openSegmentPickerRule(): EditableBusinessRule | null {
    return this.rules.find(r => r.name === this.openSegmentPickerRuleName) ?? null;
  }

  openSegmentPicker(rule: EditableBusinessRule): void {
    if (!this.canEdit() || !rule.segmentMode) return;
    this.openSegmentPickerRuleName = rule.name;
  }

  closeSegmentPicker(): void {
    this.openSegmentPickerRuleName = null;
  }

  // ── Update ────────────────────────────────────────────────────────────────

  requestUpdate(): void {
    if (!this.canEdit() || this.pendingChangesCount === 0) return;
    this.updateResult.set(null);
    this.updateError.set(null);
    this.showUpdateConfirm.set(true);
  }

  confirmUpdate(): void {
    if (!this.canEdit()) return;

    const payload: BusinessRulesModel[] = this.rules.map(({ paramEntries, editingValueIndex, segmentMode, ...rule }) => ({
      ...rule,
      segmentScope: this.normalizeSegmentScope(rule.segmentScope)
    }));

    this.updating.set(true);
    this.updateError.set(null);
    this.businessRulesService.updateSurveyBusinessRules(this.organizationId, this.surveyId, payload).subscribe({
      next: (response) => {
        this.updating.set(false);
        this.updateResult.set(response);
        this.markApplied(response.applied);
      },
      error: (err) => {
        this.updating.set(false);
        this.updateError.set(err?.message ?? 'Failed to update business rules.');
      }
    });
  }

  private markApplied(appliedNames: string[]): void {
    const applied = new Set(appliedNames);
    for (const rule of this.rules) {
      if (applied.has(rule.name)) {
        this.originalRules.set(rule.name, this.snapshotOf(rule));
      }
    }
  }

  closeUpdateDialog(): void {
    this.showUpdateConfirm.set(false);
    this.updateResult.set(null);
    this.updateError.set(null);
  }

  // ── Discard ───────────────────────────────────────────────────────────────

  requestDiscard(): void {
    if (!this.canEdit() || this.pendingChangesCount === 0) return;
    this.showDiscardConfirm.set(true);
  }

  confirmDiscard(): void {
    if (!this.canEdit()) return;
    for (const rule of this.rules) {
      this.restoreRuleFromSnapshot(rule);
    }
    this.showDiscardConfirm.set(false);
  }

  cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  private restoreRuleFromSnapshot(rule: EditableBusinessRule): void {
    const snapshot = this.originalRules.get(rule.name);
    if (!snapshot) return;

    const data = JSON.parse(snapshot) as Pick<BusinessRulesModel, 'enabled' | 'parameters' | 'values' | 'field' | 'priority' | 'segmentScope'>;

    rule.enabled = data.enabled;
    rule.parameters = data.parameters;
    rule.values = data.values;
    rule.field = data.field;
    rule.priority = data.priority;
    rule.segmentScope = data.segmentScope;

    rule.paramEntries = Object.entries(data.parameters ?? {}).map(([key, value]) => ({ key, value }));
    rule.segmentMode = this.computeSegmentMode(data.segmentScope);
    rule.editingValueIndex = null;
  }

  // ── Leave guard ─────────────────────────────────────────────────────────────

  setLeaveResolve(fn: (allow: boolean) => void): void {
    this.leaveResolve = fn;
  }

  confirmLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(true);
    this.leaveResolve = null;
  }

  cancelLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(false);
    this.leaveResolve = null;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.pendingChangesCount > 0) {
      event.preventDefault();
    }
  }
}
